import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed
  const attempts = useRef(0);
  const type = params.get("type"); // job | tool | event | (default: dashboard upgrade)
  const isListing = ["job", "tool", "event"].includes(type);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const statusUrl = type === "job" ? `/jobs/checkout/status/${sessionId}`
      : type === "tool" ? `/tools/checkout/status/${sessionId}`
      : type === "event" ? `/events/checkout/status/${sessionId}`
      : `/checkout/status/${sessionId}`;
    const poll = async () => {
      if (attempts.current >= 6) { setState("failed"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(statusUrl);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
        timer = setTimeout(poll, 2000);
      } catch { timer = setTimeout(poll, 2000); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, type]);

  const backTo = type === "tool" ? "/tools" : type === "job" ? "/jobs" : type === "event" ? "/events" : "/dashboard";
  const backLabel = type === "tool" ? "Back to Tools" : type === "job" ? "Back to Jobs" : type === "event" ? "Back to Events" : "Go to Dashboard";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="payment-return-page">
      <div className="card-surface rounded-xl p-10 text-center max-w-md w-full">
        {state === "checking" && (<>
          <Loader2 className="w-12 h-12 text-[#00F0FF] mx-auto mb-4 animate-spin" />
          <h2 className="font-display font-bold text-2xl mb-2">Confirming payment…</h2>
          <p className="text-[#94A3B8]">Please wait while we verify your transaction.</p>
        </>)}
        {state === "success" && (<>
          <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl mb-2" data-testid="payment-success">Payment confirmed</h2>
          <p className="text-[#94A3B8] mb-6">
            {isListing ? "Your listing has been received and will be reviewed within 24 hours." : "Your listing is now live with the upgraded tier."}
          </p>
          <button onClick={() => nav(backTo)} className="bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition">
            {backLabel}
          </button>
        </>)}
        {state === "failed" && (<>
          <XCircle className="w-12 h-12 text-[#EF4444] mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl mb-2" data-testid="payment-failed">Payment not completed</h2>
          <p className="text-[#94A3B8] mb-6">
            {isListing ? "Payment was not completed. Your listing was not submitted. Please try again." : "Something went wrong or the session expired."}
          </p>
          <button onClick={() => nav(backTo)} className="border border-white/15 px-6 py-3 rounded-md hover:border-[#00F0FF]/60 transition">
            {backLabel}
          </button>
        </>)}
      </div>
    </div>
  );
}
